/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
import React, { useEffect, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { cssMap } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import { Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { captureException } from '@atlassian/error-handling';

import { startAndTrackProvisioning } from '../../../../services/provisioning-api';
import {
	type PollingOptions,
	PollingTimeoutError,
	type ProvisionStatusResponse,
} from '../../../../services/provisioning-api/types';
import { ProgressBarWithMessage } from '../../components/progress-bar';
import { ProvisioningAnimations } from '../../components/provisioning-animations';
import { ProvisioningIconsAnimation } from '../../components/provisioning-icons-animation';
import { ScreenLayout } from '../../components/screen-layout';
import { useModalContext } from '../../contexts/modal-context';

const styles = cssMap({
	container: {
		paddingTop: token('space.300'),
		paddingLeft: token('space.300'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.300'),
	},
});

const DEFAULT_MAX_DURATION = 300000; // 5 minutes
const REDUCED_MAX_DURATION = 120000; // 2 minutes

const getPollingOptions = (): Omit<PollingOptions, 'signal'> => ({
	initialDelay: 0,
	interval: 1000,
	maxDuration: fg('twc-xflow-timeout-screen') ? REDUCED_MAX_DURATION : DEFAULT_MAX_DURATION,
});

type ProvisioningScreenProps = {
	onProvisioningComplete: (redirectUrl: string | undefined) => void;
};

export const ProvisioningScreen: (props: ProvisioningScreenProps) => React.JSX.Element = ({
	onProvisioningComplete,
}: ProvisioningScreenProps): React.JSX.Element => {
	const [progress, setProgress] = useState<number>(0);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const {
		cloudId,
		locale,
		sourceComponent,
		sourceContext,
		modalState: { status },
		setCurrentScreen,
	} = useModalContext();

	useEffect(() => {
		const controller = new AbortController();
		const startProvisioningAndTrackStatus = async () => {
			try {
				createAnalyticsEvent({
					eventType: 'track',
					action: status ? 'provisioningResumed' : 'provisioningStarted',
					actionSubject: 'progressBar',
					actionSubjectId: 'twcXflowModal',
					attributes: {
						sourceComponent,
						sourceContext,
					},
				}).fire('growth');
				const response = await startAndTrackProvisioning({
					cloudId,
					callback: (progress: number, statusResponse: ProvisionStatusResponse) => {
						if (!controller.signal.aborted) {
							setProgress(progress);
						}
						if (statusResponse.status === 'PROVISIONING_COMPLETED') {
							createAnalyticsEvent({
								eventType: 'track',
								action: 'provisioningCompleted',
								actionSubject: 'progressBar',
								actionSubjectId: 'twcXflowModal',
								attributes: {
									sourceComponent,
									sourceContext,
								},
							}).fire('growth');
						}
					},
				pollingOptions: {
					...getPollingOptions(),
					signal: controller.signal,
				},
					initialStatus: status,
					locale,
					sourceComponent,
					sourceContext,
				});
				if (!controller.signal.aborted) {
					setProgress(response.progress);
					if (response.status === 'PROVISIONING_COMPLETED') {
						setTimeout(() => {
							if (!controller.signal.aborted) {
								const redirectUrl = response.context?.onboardingUrl;
								if (!redirectUrl) {
									captureException(
										'redirect url not provided',
										new Error('Redirect url not provided'),
										{
											packageName: '@atlassian/twc-xflow-modal',
											teamName: 'starburst',
										},
									);
								}
								onProvisioningComplete(redirectUrl);
							}
						}, 1000);
					}
				}
			} catch (error) {
				if (!controller.signal.aborted) {
					if (PollingTimeoutError.is(error) && fg('twc-xflow-timeout-screen')) {
						createAnalyticsEvent({
							eventType: 'track',
							action: 'provisioningTimedOut',
							actionSubject: 'progressBar',
							actionSubjectId: 'twcXflowModal',
							attributes: {
								sourceComponent,
								sourceContext,
								maxDuration: error.maxDuration,
							},
						}).fire('growth');
						setCurrentScreen('timeout');
					} else {
						captureException(
							'Provisioning failed - showing error screen',
							new Error(error instanceof Error ? error.name : 'Unknown error'),
							{
								packageName: '@atlassian/twc-xflow-modal',
								teamName: 'starburst',
							},
						);
						setCurrentScreen('error');
					}
				}
			}
		};

		startProvisioningAndTrackStatus();

		return () => {
			controller.abort();
		};
	}, [
		cloudId,
		setCurrentScreen,
		onProvisioningComplete,
		createAnalyticsEvent,
		status,
		locale,
		sourceComponent,
		sourceContext,
	]);

	useEffect(() => {
		createAnalyticsEvent({
			eventType: 'screen',
			name: 'twcXflowModalProvisioningScreen',
			attributes: {
				sourceComponent,
				sourceContext,
			},
		}).fire('growth');
	}, [createAnalyticsEvent, sourceComponent, sourceContext]);

	return (
		<ScreenLayout testId="provisioning-screen">
			<ProvisioningAnimations />
			<ScreenLayout.Content>
				<Stack xcss={styles.container} alignBlock="center" alignInline="center">
					<ProvisioningIconsAnimation />
					<ProgressBarWithMessage progress={progress} />
				</Stack>
			</ScreenLayout.Content>
		</ScreenLayout>
	);
};
